// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import PageLoader from '../../../../components/PageLoader';
import { fetchSkillAssessmentReport } from '../../../../store/interviewHostSlice';
import styles from './SkillAssessmentReport.module.scss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';

const SkillAssessmentReport: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { skillAssessmentReport, skillAssessmentReportLoading, skillAssessmentReportError } = useSelector(
    (state: RootState) => state.interviewHost
  );

  useEffect(() => {
    if (candidateId) {
      dispatch(fetchSkillAssessmentReport({ candidateId }));
    }
  }, [candidateId, dispatch]);

  const handleBreadcrumbClick = useCallback(
    (href: string | null) => {
      if (href) {
        navigate(href);
      }
    },
    [navigate]
  );

  const getInitials = useCallback((name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, []);

  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, []);

  const formatDeadline = useCallback((dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  }, []);

  const getLevelColor = useCallback((level: 'strong' | 'developing' | 'gap') => {
    switch (level) {
      case 'strong':
        return '#2e7d32';
      case 'developing':
        return '#f57c00';
      case 'gap':
        return '#d32f2f';
      default:
        return '#757575';
    }
  }, []);

  const getLevelLabel = useCallback((level: 'strong' | 'developing' | 'gap') => {
    switch (level) {
      case 'strong':
        return '81-100 Strong';
      case 'developing':
        return '21-60 Developing';
      case 'gap':
        return '0-20 Gap';
      default:
        return '';
    }
  }, []);

  const getScoreColor = useCallback((score: number) => {
    if (score >= 81) return { primary: '#2e7d32', light: '#a5d6a7', dark: '#1b5e20' };
    if (score >= 61) return { primary: '#689f38', light: '#c5e1a5', dark: '#558b2f' };
    if (score >= 21) return { primary: '#f57c00', light: '#ffcc80', dark: '#e65100' };
    return { primary: '#d32f2f', light: '#ef9a9a', dark: '#c62828' };
  }, []);

  if (skillAssessmentReportLoading) {
    return <PageLoader label="Loading skill assessment report..." activeNav="home" showSidebar={false} />;
  }

  if (skillAssessmentReportError || !skillAssessmentReport) {
    return (
      <div className={styles.container}>
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load skill assessment report</h2>
            <p>{skillAssessmentReportError || 'An unexpected error occurred.'}</p>
            <button onClick={() => candidateId && dispatch(fetchSkillAssessmentReport({ candidateId }))}>
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { header, overall, skill_area_scores, assessment_details, gap_summary } = skillAssessmentReport;

  return (
    <div className={styles.container}>
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          {header.breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {crumb.href ? (
                <button className={styles.breadcrumbLink} onClick={() => handleBreadcrumbClick(crumb.href)}>
                  {crumb.label}
                </button>
              ) : (
                <span className={styles.breadcrumbCurrent}>{crumb.label}</span>
              )}
              {index < header.breadcrumbs.length - 1 && <ChevronRightIcon className={styles.breadcrumbSeparator} />}
            </React.Fragment>
          ))}
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}>{header.candidate_initials}</div>
            <div className={styles.headerInfo}>
              <h1 className={styles.candidateName}>{header.candidate_name}</h1>
              <p className={styles.candidateMeta}>{header.subtitle}</p>
            </div>
          </div>
          <div className={styles.headerButtons}>
            <button className={styles.btnSecondary} onClick={() => window.open(header.download_report_url, '_blank')}>
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              Download report
            </button>
            <button className={styles.btnPrimary} onClick={() => navigate(header.interview_report_url)}>
              <DescriptionIcon sx={{ fontSize: 18 }} />
              View interview report
            </button>
          </div>
        </div>

        {/* Score and Summary */}
        <div className={styles.topSection}>
          <div className={styles.scoreCircle}>
            <svg className={styles.circularProgress} viewBox="0 0 120 120">
              <circle className={styles.circleBackground} cx="60" cy="60" r="54" />
              <circle
                className={styles.circleProgress}
                cx="60"
                cy="60"
                r="54"
                style={{
                  strokeDasharray: `${((overall.score || 0) / 100) * 339.292} 339.292`,
                  stroke: getScoreColor(overall.score || 0).primary
                }}
              />
            </svg>
            <div className={styles.scoreText}>
              <span className={styles.scoreNumber} style={{ color: getScoreColor(overall.score || 0).primary }}>
                {overall.score || 0}
              </span>
              <span className={styles.scoreLabel}>{overall.label}</span>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className={styles.mainGrid}>
          {/* Left Column - Skill Area Scores */}
          <div className={styles.leftColumn}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📊</span>
                Skill area scores
              </h3>
              <div className={styles.skillList}>
                {skill_area_scores.map((skill, index) => (
                  <div key={index} className={styles.skillItem}>
                    <div className={styles.skillHeader}>
                      <span className={styles.skillName}>{skill.skill}</span>
                      <span className={styles.skillScore} style={{ color: getLevelColor(skill.level) }}>
                        {skill.score}
                      </span>
                    </div>
                    <div className={styles.skillBar}>
                      <div
                        className={styles.skillFill}
                        style={{
                          width: `${(skill.score / skill.max_score) * 100}%`,
                          background: getLevelColor(skill.level)
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.legend}>
                <div className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ background: '#2e7d32' }} />
                  <span>81-100 Strong</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ background: '#f57c00' }} />
                  <span>21-60 Developing</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ background: '#d32f2f' }} />
                  <span>0-20 Gap</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Assessment Details */}
          <div className={styles.rightColumn}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📋</span>
                Assessment details
              </h3>
              <div className={styles.detailsList}>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>📧</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>Sent to candidate</span>
                    <span className={styles.detailValue}>{formatDate(assessment_details.sent_to_candidate_at)}</span>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>⏰</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>Deadline</span>
                    <span className={styles.detailValue}>{formatDeadline(assessment_details.deadline_at)}</span>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>⏱️</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>Completed in</span>
                    <span className={styles.detailValue}>
                      {assessment_details.completed_in_minutes
                        ? `${assessment_details.completed_in_minutes} minutes`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>🎯</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>Role used</span>
                    <span className={styles.detailValue}>{assessment_details.benchmark_used || 'N/A'}</span>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>🔄</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>Dynamic per role</span>
                    <span className={styles.detailValue}>{assessment_details.dynamic_per_role ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gap Summary */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>💡</span>
            Gap summary
          </h3>
          <div className={styles.gapSummary}>
            {/* Strengths */}
            {gap_summary.strengths.length > 0 && (
              <div className={styles.gapSection}>
                <h4 className={styles.gapSectionTitle}>
                  <span className={styles.gapTitleText}>
                    Strong: {gap_summary.strengths.map((s) => s.title).join(' & ')}
                  </span>
                </h4>
                {gap_summary.strengths.map((strength, index) => (
                  <div key={index} className={styles.gapItem}>
                    <div className={styles.gapIcon}>▸</div>
                    <p className={styles.gapText}>{strength.evidence || strength.title}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Gaps */}
            {gap_summary.gaps.length > 0 && (
              <div className={styles.gapSection}>
                <h4 className={styles.gapSectionTitle}>
                  <span className={styles.gapTitleText} style={{ color: '#d32f2f' }}>
                    Gap: {gap_summary.gaps.map((g) => g.title).join(', ')}
                  </span>
                </h4>
                {gap_summary.gaps.map((gap, index) => (
                  <div key={index} className={styles.gapItem}>
                    <div className={styles.gapIcon} style={{ color: '#d32f2f' }}>
                      ⚠
                    </div>
                    <p className={styles.gapText}>{gap.evidence || gap.title}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SkillAssessmentReport;
